(function($) {
	'use strict';

	/**
	 * CRF Domain calculation function
	 * 
	 * This function calculates domain scores for CRF frameworks across different years.
	 * It's designed to be flexible to handle different topic categories and mappings
	 * between years (2021, 2022, 2023, 2024, 2025, etc.).
	 * 
	 * Usage in different years:
	 * - CRFS 2025: Uses default parameters (modern topic categories)
	 * - CRFS 2021: Pass custom legacyTopicCategories for 2021-specific topics
	 * - CRFS 2022: Pass custom domainToClassMap and governanceDomainMap for 2022-specific CSS classes
	 * 
	 * @param {Array} processedItems - Array of processed items with maturity scores
	 * @param {Object} qualityItems - Quality items structure organized by category and topic
	 * @param {boolean} isLegacyMode - Whether to run in legacy mode
	 * @param {Object} legacyTopicCategories - Topic categories for legacy mode (optional, has defaults)
	 * @param {Object} domainToClassMap - Mapping of domain names to CSS class suffixes (optional, has defaults)
	 * @param {Object} governanceDomainMap - Mapping of governance domains for scope table (optional, has defaults)
	 * @param {Object} topicToTableClassSuffix - Optional map from normalized topic name to table cell class suffix (e.g. 'software development standardization' -> 'software-development-management') so API topic names match table DOM classes
	 */
	function crfDomain(processedItems, qualityItems, isLegacyMode = true, legacyTopicCategories = null, domainToClassMap = null, governanceDomainMap = null, topicToTableClassSuffix = null) {
		//const TOPICS_TO_DEBUG = ['resilience management', 'physical security management', 'privacy management'];
		const TOPICS_TO_DEBUG = [];

		//console.log('[crfDomain] Starting with processedItems:', processedItems);
		//console.log('[crfDomain] qualityItems:', qualityItems);
		//console.log('[crfDomain] Is legacy mode:', isLegacyMode);
		
		const domainScores = {};
		const domainRawData = {};
		const processedTopics = new Set();
		const topicsWithValidData = new Set();
		const topicApplicabilityCounts = {};

		// Helper function to normalize topic names
		const normalizeTopicName = (topic) => {
			return topic.replace(/-/g, ' ').toLowerCase();
		};

		// Helper function to check if we should debug this topic
		const shouldDebugTopic = (topic) => {
			return TOPICS_TO_DEBUG.includes(topic.toLowerCase());
		};
		
		// Default legacy topic categories if not provided
		const defaultLegacyTopicCategories = {
			'Governance': [
				'Governance',
				'Threat Management',
				'Security Policy Management',
				'Education and Awareness',
				'Project Management',
				'Exception Management',
				'Metrics Management',
				'Audit Management',
				'Third Party Management',
				'Risk Reporting',
				'Personnel Management',
				'Physical Security',
				'Physical Security Management',
				'Business Continuity',
				'Incident Management',
				'Data Privacy',
				'Privacy Management',
				'Resilience Management'
			],
			'Technical': [
				'Asset Inventory',
				'Software Inventory',
				'Application Control',
				'Patch Management',
				'Vulnerability Management',
				'Configuration Management',
				'Endpoint Protection',
				'Removable Media Protection',
				'Mobile Device Protection',
				'Backup and Recovery',
				'Log Management',
				'File Integrity Management',
				'Identity Management',
				'Data Inventory Management',
				'Access Management',
				'Privileged Account Management',
				'Network Device Management',
				'Boundary Filtering',
				'Remote Access',
				'Web Filtering',
				'Email Filtering',
				'Network Segmentation',
				'Wireless',
				'Software Development',
				'Static Code Analysis'
			]
		};

		// Use provided categories or defaults
		const topicCategories = legacyTopicCategories || defaultLegacyTopicCategories;

		// [crfDomain] Debug: log qualityItems topics (helps trace SDM/SDO mismatch)
		if (!isLegacyMode && Object.keys(qualityItems).length) {
			const topicList = [];
			Object.keys(qualityItems).forEach(cat => {
				Object.keys(qualityItems[cat]).forEach(t => { topicList.push({ category: cat, topic: t }); });
			});
			console.log('[crfDomain] qualityItems topics:', topicList.map(x => x.category + ' / ' + x.topic));
			console.log('[crfDomain] topicToTableClassSuffix provided:', !!topicToTableClassSuffix, topicToTableClassSuffix || '');
		}

		// First pass: Count total items per topic and collect raw data
		Object.keys(qualityItems).forEach(category => {
			Object.keys(qualityItems[category]).forEach(topic => {
				const safeguards = qualityItems[category][topic];
				const normalizedTopic = normalizeTopicName(topic);
				
				// Initialize the topic data structure
				topicApplicabilityCounts[normalizedTopic] = {
					total: safeguards.length,
					notApplicable: 0,
					processedCount: 0,
					originalTopic: topic // Store original topic name for later use
				};
				
				// Initialize domain raw data structure
				if (!domainRawData[topic]) {
					domainRawData[topic] = {
						totalScore: 0,
						totalCount: 0,
						potentialCount: safeguards.length // Track the potential count
					};
				} else {
					domainRawData[topic].potentialCount = safeguards.length;
				}
			});
		});

		// Second pass: Process items and count not applicable ones
		processedItems.forEach(item => {
			const safeguardId = item.safeguardid.S;
			const maturityScore = item.maturityscore;
			const policyStatus = item.policystatus ? parseInt(item.policystatus.N, 10) : null;

			// Find the matching safeguard in qualityItems
			Object.keys(qualityItems).forEach(category => {
				Object.keys(qualityItems[category]).forEach(topic => {
					const safeguards = qualityItems[category][topic];
					const matchingSafeguard = safeguards.find(s => s.safeguardid.S === safeguardId);
					
					if (matchingSafeguard) {
						const normalizedTopic = normalizeTopicName(topic);
						
						// Count not applicable items
						if (policyStatus === 1) {
							topicApplicabilityCounts[normalizedTopic].notApplicable++;
							// Decrease potential count for Not Applicable items
							domainRawData[topic].potentialCount--;
						} else if (maturityScore !== null) {
							// Add to processed topics
							processedTopics.add(topic);
							topicApplicabilityCounts[normalizedTopic].processedCount++;

							// Add to domain totals
							domainRawData[topic].totalScore += maturityScore;
							domainRawData[topic].totalCount += 1;

							// Mark domain as having valid data
							topicsWithValidData.add(topic);
						}
					}
				});
			});
		});

		// Calculate scores for each domain (use topicToTableClassSuffix so API topic names map to table cell classes, e.g. 2026 SDM/SDO)
		Object.keys(topicApplicabilityCounts).forEach(normalizedTopic => {
			const originalTopic = topicApplicabilityCounts[normalizedTopic].originalTopic;
			const topicData = domainRawData[originalTopic] || { totalScore: 0, totalCount: 0, potentialCount: 0 };
			const tableSuffix = (topicToTableClassSuffix && topicToTableClassSuffix[normalizedTopic]) || normalizedTopic.replace(/\s+/g, '-');
			const topicKey = 'crf-domain-' + tableSuffix;

			const isDevTopic = /software development|development operations|standardization/i.test(originalTopic);
			if (isDevTopic) {
				console.log('[crfDomain] score build:', {
					originalTopic,
					normalizedTopic,
					tableSuffix,
					topicKey,
					totalScore: topicData.totalScore,
					potentialCount: topicData.potentialCount,
					mapped: !!(topicToTableClassSuffix && topicToTableClassSuffix[normalizedTopic])
				});
			}

			// Only process if not all items are not applicable
			if (topicApplicabilityCounts[normalizedTopic].notApplicable < topicApplicabilityCounts[normalizedTopic].total) {
				// Calculate average using potentialCount instead of totalCount
				if (topicData.potentialCount > 0) {
					const topicAverage = (topicData.totalScore / topicData.potentialCount) * 5;
					domainScores[topicKey] = topicAverage.toFixed(2);
					if (isDevTopic) console.log('[crfDomain] score set:', topicKey, '=', domainScores[topicKey]);
				} else {
					if (shouldDebugTopic(originalTopic)) {
						console.log(`[crfDomain] No valid data found for topic ${originalTopic}`);
					}
					domainScores[topicKey] = 0;
				}
			} else {
				if (shouldDebugTopic(originalTopic)) {
					console.log(`[crfDomain] All items are not applicable for topic ${originalTopic}`);
				}
			}
		});

		if (!isLegacyMode && topicToTableClassSuffix) {
			console.log('[crfDomain] domainScores keys (software-development*):', Object.keys(domainScores).filter(k => k.indexOf('software-development') !== -1));
			Object.keys(domainScores).filter(k => k.indexOf('software-development') !== -1).forEach(k => {
				console.log('[crfDomain] domainScores[' + k + '] =', domainScores[k]);
			});
		}

		// Calculate overall score
		let overallTotalScore = 0;
		let overallPotentialCount = 0;

		Object.values(domainRawData).forEach(data => {
			overallTotalScore += data.totalScore;
			overallPotentialCount += data.potentialCount;
		});

		let overallScore;
		if (overallPotentialCount > 0) {
			overallScore = ((overallTotalScore / overallPotentialCount) * 5).toFixed(2);
			console.log(`[crfDomain] Overall true average: ${overallScore} (totalScore: ${overallTotalScore}, potentialCount: ${overallPotentialCount})`);
		} else {
			const validScores = Object.values(domainScores).filter(score => parseFloat(score) > 0);
			overallScore = validScores.length > 0 ?
				(validScores.reduce((a, b) => a + parseFloat(b), 0) / validScores.length).toFixed(2) :
				"0.00";
			console.log(`[crfDomain] Using traditional average for overall score: ${overallScore}`);
		}

		// Expose domain-based overall so non-table contexts (e.g. dashboard mark-complete)
		// can use the same source of truth without reading DOM rows.
		if (!isLegacyMode && typeof window !== 'undefined') {
			window.overallCRFDomainScore = overallScore;
		}
		
		if (isLegacyMode) {
			// For legacy mode, we'll calculate scores for governance and technical categories
			const legacyScores = {
				'governance': { totalScore: 0, count: 0 },
				'technical': { totalScore: 0, count: 0 }
			};
			
			// Map each topic to its legacy category and accumulate scores
			Object.keys(domainScores).forEach(topicKey => {
				const normalizedKey = topicKey.replace('crf-domain-', '');
				const originalTopicName = Object.keys(topicApplicabilityCounts).find(
					key => normalizeTopicName(topicApplicabilityCounts[key].originalTopic).replace(/\s+/g, '-') === normalizedKey
				);
				
				if (originalTopicName) {
					const originalTopic = topicApplicabilityCounts[originalTopicName].originalTopic;
					
					// Determine which legacy category this topic belongs to
					let legacyCategory = null;
					if (topicCategories.Governance.includes(originalTopic)) {
						legacyCategory = 'governance';
					} else if (topicCategories.Technical.includes(originalTopic)) {
						legacyCategory = 'technical';
					}
					
					if (legacyCategory) {
						legacyScores[legacyCategory].totalScore += parseFloat(domainScores[topicKey]);
						legacyScores[legacyCategory].count++;
					} else {
						console.log(`[crfDomain] Topic ${originalTopic} not found in legacy categories`);
					}
				}
			});
			
			// Calculate average scores for each legacy category
			const governanceScore = legacyScores.governance.count > 0 ? 
				(legacyScores.governance.totalScore / legacyScores.governance.count).toFixed(2) : "0.00";
			const technicalScore = legacyScores.technical.count > 0 ? 
				(legacyScores.technical.totalScore / legacyScores.technical.count).toFixed(2) : "0.00";
			
			console.log(`[crfDomain] Legacy governance score: ${governanceScore}, technical score: ${technicalScore}`);
			
			// Default domain to class mapping if not provided
			const defaultDomainToClassMap = {
				'Asset Inventory': 'asset-inventory-discovery',
				'Software Inventory': 'software-inventory-discovery',
				'Application Control': 'application-control',
				'Patch Management': 'patch-management',
				'Vulnerability Management': 'vulnerability-management',
				'Configuration Management': 'configuration-management',
				'Endpoint Protection': 'endpoint-protection',
				'Removable Media Protection': 'removable-media-protection',
				'Mobile Device Protection': 'mobile-device-protection',
				'Backup and Recovery': 'backup-recovery',
				'Log Management': 'log-management',
				'File Integrity Management': 'file-integrity-management',
				'Identity Management': 'identity-management',
				'Data Inventory Management': 'data-inventory',
				'Data Inventory': 'data-inventory',
				'Access Management': 'access-management',
				'Privileged Account Management': 'privileged-account-management',
				'Network Device Management': 'network-device-management',
				'Boundary Filtering': 'boundary-filtering',
				'Remote Access': 'remote-access',
				'Web Filtering': 'web-filtering',
				'Email Filtering': 'email-filtering',
				'Network Segmentation': 'network-segmentation-control',
				'Wireless': 'wireless-access',
				'Software Development': 'software-development-management',
				'Static Code Analysis': 'static-code-analysis'
			};
			
			const classMap = domainToClassMap || defaultDomainToClassMap;
			
			// Loop through technical domains and update their scores
			Object.keys(classMap).forEach(domain => {
				const selector = `.crf-legacy-domain-${classMap[domain]}`;
				const normalizedDomain = normalizeTopicName(domain);
				const applicabilityCount = topicApplicabilityCounts[normalizedDomain];

			if (applicabilityCount && applicabilityCount.notApplicable >= applicabilityCount.total && applicabilityCount.total > 0) {
				console.log(`[Legacy Debug] Hiding row for domain: ${domain}`);
				$(selector).closest('tr').addClass('domain-all-na').hide();
				return; // continue to next domain
			}

			const domainMatches = Object.keys(domainRawData).filter(key => 
				key.toLowerCase().includes(domain.toLowerCase())
			);
			
			if (domainMatches.length > 0) {
				const topicData = domainRawData[domainMatches[0]];
				if (topicData.potentialCount > 0) {
					const score = ((topicData.totalScore / topicData.potentialCount) * 5).toFixed(2);
					$(selector).text(score);
				} else {
					$(selector).text("0.00");
				}
			} else {
				$(selector).text("0.00");
			}
		});
		
		// Default governance domain mapping if not provided
		const defaultGovernanceDomainMap = {
			'Physical Security Management': 'physical',
			'Privacy Management': 'privacy',
			'Resilience Management': 'resilience'
		};
		
		const governanceMap = governanceDomainMap || defaultGovernanceDomainMap;
		
		// Handle governance topics that should appear in the legacy scope table
		Object.keys(governanceMap).forEach(domain => {
			const selector = `.crf-scope-${governanceMap[domain]}`;
			const normalizedDomain = normalizeTopicName(domain);
			const applicabilityCount = topicApplicabilityCounts[normalizedDomain];

			if (applicabilityCount && applicabilityCount.notApplicable >= applicabilityCount.total && applicabilityCount.total > 0) {
				console.log(`[Legacy Debug] Hiding scope row for domain: ${domain}`);
				$(selector).closest('tr').addClass('domain-all-na').hide();
					return; // continue to next domain
				}

				const domainMatches = Object.keys(domainRawData).filter(key => 
					key.toLowerCase().includes(domain.toLowerCase())
				);
				
				if (domainMatches.length > 0) {
					const topicData = domainRawData[domainMatches[0]];
					if (topicData.potentialCount > 0) {
						const score = ((topicData.totalScore / topicData.potentialCount) * 5).toFixed(2);
						console.log(`[Legacy Scope Score] Domain: ${domain}`, {
							totalScore: topicData.totalScore,
							potentialCount: topicData.potentialCount,
							calculatedScore: score
						});
						$(selector).text(score);
					} else {
						$(selector).text("0.00");
					}
				} else {
					$(selector).text("0.00");
				}
			});
			
			// Update the overall score
			$('.crf-domain-overall').text(overallScore);
		} else {
		// Standard mode - update the DOM with calculated scores and hide rows with no data
		$('[class^="crf-domain-"]').each(function () {
			const cellClass = $(this).attr('class');

				// Skip the overall score
				if (cellClass === 'crf-domain-overall') {
					return;
				}

				// Extract the topic name from the class
				const topicSlug = cellClass.replace('crf-domain-', '');
				const normalizedTopic = topicSlug.replace(/-/g, ' ');

				if (shouldDebugTopic(normalizedTopic)) {
					console.log(`[crfDomain] Checking table cell for topic: ${normalizedTopic}, class: ${cellClass}`);
				}

				// Check if this topic exists in our data (including via topicToTableClassSuffix map, e.g. API "Software Development Standardization" -> table "software-development-management")
				const topicExists = Object.keys(qualityItems).some(category =>
					Object.keys(qualityItems[category]).some(topic => {
						const norm = normalizeTopicName(topic);
						const slug = norm.replace(/\s+/g, '-');
						if (slug === topicSlug) return true;
						if (topicToTableClassSuffix && topicToTableClassSuffix[norm] === topicSlug) return true;
						return false;
					})
				);

				if (!topicExists) {
					if (shouldDebugTopic(normalizedTopic)) {
						console.log(`[crfDomain] Topic ${normalizedTopic} not found in quality items`);
					}
					$(this).text("0.00");
					return;
				}

				// When using topicToTableClassSuffix, scores are keyed by table class; find the qualityItems key that maps to this cell for applicability
				let normalizedTopicForCount = normalizedTopic;
				if (topicToTableClassSuffix) {
					const mappedNorms = Object.keys(topicToTableClassSuffix).filter(n => topicToTableClassSuffix[n] === topicSlug);
					// Use the mapped key that actually exists in topicApplicabilityCounts (e.g. 'software development operations (devops)' not 'software development operations management')
					const existing = mappedNorms.find(n => topicApplicabilityCounts[n]);
					normalizedTopicForCount = existing || mappedNorms[0] || normalizedTopic;
				}
				// Check if this topic should be shown
				const applicabilityCount = topicApplicabilityCounts[normalizedTopicForCount];
				const isSoftwareDevCell = topicSlug.indexOf('software-development') !== -1;
				if (isSoftwareDevCell) {
					console.log('[crfDomain] table cell:', {
						topicSlug,
						cellClass,
						topicExists,
						normalizedTopicForCount,
						applicabilityCount: applicabilityCount ? { total: applicabilityCount.total, notApplicable: applicabilityCount.notApplicable } : null,
						domainScoresValue: domainScores[cellClass],
						'all domainScores keys': Object.keys(domainScores).filter(k => k.indexOf('software') !== -1)
					});
				}
		if (applicabilityCount) {
			if (applicabilityCount.notApplicable < applicabilityCount.total) {
					$(this).closest('tr').removeClass('domain-all-na').show();
					$(this).text(domainScores[cellClass] || "0.00");
				} else {
					$(this).closest('tr').addClass('domain-all-na').hide();
				}
			} else {
				$(this).closest('tr').removeClass('domain-all-na').show();
				$(this).text("0.00");
			}
			});

			$('.crf-domain-overall').text(overallScore);
		}
	}

	// Export for use in other modules
	if (typeof module !== 'undefined' && module.exports) {
		module.exports = { crfDomain };
	} else if (typeof window !== 'undefined') {
		window.crfDomain = crfDomain;
	}

})(jQuery);
